import { useMemo, useState } from 'react';
import { Search, Plus, Pencil, Shield, Mic, Package, Star, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const ROLE_META = {
  admin:       { label: 'Admin',       icon: Shield,  cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  interviewer: { label: 'Interviewer', icon: Mic,     cls: 'bg-blue-50 text-blue-700 border-blue-100' },
  production:  { label: 'Production',  icon: Package, cls: 'bg-amber-50 text-amber-700 border-amber-100' },
};

const SortIcon = ({ dir }) => {
  if (dir === 'asc') return <ChevronUp size={11} className="text-slate-700" />;
  if (dir === 'desc') return <ChevronDown size={11} className="text-slate-700" />;
  return <ChevronsUpDown size={11} className="text-slate-400" />;
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
}

export default function TeamTable({ users, interviewers, onAdd, onEdit }) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all | admin | interviewer | production | inactive
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const adminCount = users.filter(u => (u.roles || [u.role]).includes('admin')).length;
  const prodCount  = users.filter(u => (u.roles || [u.role]).includes('production')).length;

  const visible = useMemo(() => {
    let arr = users.slice();
    if (roleFilter === 'inactive') arr = arr.filter(u => !u.active);
    else if (roleFilter !== 'all') arr = arr.filter(u => (u.roles || [u.role]).includes(roleFilter) && u.active !== false);
    else arr = arr.filter(u => u.active !== false || true); // keep all by default
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(u =>
        (u.name || '').toLowerCase().includes(s) ||
        (u.email || '').toLowerCase().includes(s)
      );
    }
    const getKey = {
      name:   u => (u.name || '').toLowerCase(),
      email:  u => (u.email || '').toLowerCase(),
      status: u => (u.active ? 0 : 1),
    }[sortKey] || (u => (u.name || '').toLowerCase());
    arr.sort((a, b) => {
      const va = getKey(a); const vb = getKey(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [users, search, roleFilter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  return (
    <div className="px-6 py-5" data-testid="team-table">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { key: 'admin',       icon: Shield,  label: 'Admins',       count: adminCount },
          { key: 'interviewer', icon: Mic,     label: 'Interviewers', count: interviewers.length },
          { key: 'production',  icon: Package, label: 'Production',   count: prodCount },
        ].map(s => {
          const I = s.icon;
          return (
            <div key={s.key} data-testid={`stat-${s.label.toLowerCase()}`} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-500 mb-2">
                <I size={14} />
                <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500">{s.label}</span>
              </div>
              <p className="text-[22px] font-semibold text-slate-900 tracking-tight leading-none">{s.count}</p>
            </div>
          );
        })}
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[14px] font-semibold text-slate-900">Team members</h2>
          <p className="text-[12px] text-slate-500 mt-0.5">Internal accounts with access to Heirloom Ops.</p>
        </div>
        <button
          onClick={onAdd}
          data-testid="create-user-button"
          className="h-8 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md text-[13px] font-medium shadow-sm transition-colors"
        >
          <Plus size={13} /> Add user
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="search-team"
            className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md pl-8 pr-2.5 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
          />
        </div>
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          data-testid="filter-team-role"
          className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
        >
          <option value="all">All roles</option>
          <option value="admin">Admins</option>
          <option value="interviewer">Interviewers</option>
          <option value="production">Production</option>
          <option value="inactive">Inactive only</option>
        </select>
        <span className="text-[12px] text-slate-500 sm:ml-1" data-testid="team-count">{visible.length} member{visible.length === 1 ? '' : 's'}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        {visible.length === 0 ? (
          <div className="text-center py-16 px-6">
            <p className="text-[14px] text-slate-700 font-medium mb-1">No users match the current filters</p>
            <p className="text-[12px] text-slate-500">Try clearing filters or add a new user.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                  <th data-sortable="true" data-testid="team-sort-name" onClick={() => toggleSort('name')} className="w-[28%] px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 hover:text-slate-900">User <SortIcon dir={sortKey === 'name' ? sortDir : null} /></span>
                  </th>
                  <th className="w-[26%] px-4 py-2.5"><span>Roles</span></th>
                  <th className="w-[14%] px-4 py-2.5"><span>Flag</span></th>
                  <th data-sortable="true" data-testid="team-sort-status" onClick={() => toggleSort('status')} className="w-[12%] px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 hover:text-slate-900">Status <SortIcon dir={sortKey === 'status' ? sortDir : null} /></span>
                  </th>
                  <th className="w-[20%] px-4 py-2.5 text-right pr-4"><span></span></th>
                </tr>
              </thead>
              <tbody className="text-[13px] text-slate-700">
                {visible.map(u => {
                  const roles = (u.roles || [u.role]).filter(Boolean);
                  return (
                    <tr
                      key={u._id}
                      data-testid={`user-row-${u._id}`}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-slate-900 text-white grid place-items-center text-[10px] font-semibold shrink-0">
                            {initials(u.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate leading-tight">{u.name}</p>
                            <p className="text-[12px] text-slate-500 truncate leading-tight">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {roles.map(r => {
                            const m = ROLE_META[r] || { label: r, icon: Shield, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                            const RI = m.icon;
                            return (
                              <span key={r} className={`ops-pill ${m.cls}`}>
                                <RI size={10} /> {m.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {u.is_production_lead && (
                          <span data-testid={`lead-badge-${u._id}`} className="ops-pill bg-amber-50 text-amber-700 border-amber-100">
                            <Star size={10} /> Production Lead
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {u.active === false ? (
                          <span className="ops-pill bg-red-50 text-red-700 border-red-100">Inactive</span>
                        ) : (
                          <span className="ops-pill bg-emerald-50 text-emerald-700 border-emerald-100">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => onEdit(u)}
                          data-testid={`edit-user-${u._id}`}
                          className="inline-flex items-center gap-1 text-[12px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded px-2.5 py-1 transition-colors"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
